<template>
  <div class="storeFinder entity-list flex flex-col gap-4">
    <template
      v-for="(entry, index) in storeList"
      :key="index"
    >
      <div
        class="entity active w-full rounded-3xl border p-4"
        :class="index === selected ? ' selected' : ''"
        @click="selectNewLocalStore(index)"
      >
        <div>
          <BasicIcon
            :name="'map-pin-solid'"
            class="w-3 text-primary py-1"
          />
        </div>
        <div class="location">
          <div class="address">
            <div class="title">
              <div>{{ entry.store.name }}</div>
              <div
                v-if="(entry.linearDistance / 1000).toFixed(1) != '0.0'"
                class="distance flex items-center"
              >
                <BasicIcon
                  :name="'location-dot-solid'"
                  class="w-4 text-secondary"
                />&nbsp;{{ (entry.linearDistance / 1000).toFixed(1) }} km
              </div>
            </div>
            <div>{{ entry.store.street }}</div>
            <div>{{ entry.store.zip }} {{ entry.store.city }}</div>
            <div v-if="index === selected">{{ entry.store.phone }}</div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="getCurrentOpenings(entry.store)" />
            <a
              v-if="index !== selected"
              class="text-primary cursor-pointer"
              >weitere Details</a
            >
          </div>
          <div
            v-if="index === selected"
            class="opening-times"
          >
            <template
              v-for="days in Object.keys(entry.openingTimes)"
              :key="days"
            >
              <template v-if="entry.openingTimes[days]?.times">
                <strong style="text-transform: capitalize">{{ days }}</strong>
                <template
                  v-for="times in entry.openingTimes[days].times"
                  :key="times"
                >
                  <div>{{ times }}</div>
                </template>
              </template>
            </template>
          </div>
          <template v-if="storeRef?.currentStore?.value?.id !== entry.store.id">
            <div
              v-if="index === selected"
              class="assign-trigger"
            >
              <button
                class="rounded-full font-bold"
                @click="changeLocalStoreInCookie(entry.store.id)"
              >
                Markt auswählen >
              </button>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import { ProvideNames } from '#shared/statics';
import { ref } from 'vue';
import { convertMinutesToTimeString } from '~~/shared/utils/storeMapper';
import type { SimpleStore, StoreDistance } from '~~/shared/types/Store';

const props = defineProps({
  storeList: {
    type: Array<StoreDistance>,
    required: true,
  },
});

const emit = defineEmits(['clickedOnStore', 'storeChanged', 'selectStore']);

const route = useRoute();
const storeRef = inject(ProvideNames.CURRENT_STORE_KEY);
const updateFunction = storeRef?.updateFunction || console.error;
const selected = ref(0);

function selectNewLocalStore(index: number) {
  selected.value = index;
  emit('clickedOnStore', props.storeList[selected.value]?.store);
}

function selectStore(marker: { id: string }) {
  const idx = props.storeList.findIndex((dist) => dist.store.id == marker.id);
  if (idx > -1) {
    selectNewLocalStore(idx);
    setTimeout(function () {
      document.querySelector('.storeFinder.entity-list .entity.selected')?.scrollIntoView();
    });
  }
}

defineExpose({ selectStore });

async function changeLocalStoreInCookie(storeId: string) {
  try {
    emit('storeChanged', storeId);
    const response = await useFetch('/api/neo/frontend/_api/storeFinder/assignStore', {
      method: 'POST',
      body: {
        storeId: storeId,
        redirectUrl: '/',
      },
      headers: {
        'csrf-token': useCurrentUserState()?.value?.csrfToken ?? '',
        'bt-use-user-auth': 'true',
      },
      server: false,
    });

    await useFetch('/api/neo/frontend/_api/user/getCsrfToken', {
      method: 'GET',
      query: {
        branch_id: storeId,
      },
      headers: {
        'bt-use-user-auth': 'true',
      },
    });

    clearNuxtData('expUserProfile');
    clearNuxtState(['currentShoppingCart', 'currentReservationCart', 'userProfile']);

    nextTick(() => {
      updateFunction(storeId);
    });

    const storeSlug = useRoute().params?.storeslug;
    if (storeSlug && typeof storeSlug === 'string') {
      const store = await $fetch<Store>('/api/store', {
        query: {
          storeId: storeId,
        },
      });

      return navigateTo({
        path: useRoute().path.replace(/(^\/markt\/)(\w*)/, `$1${store?.storeLink?.toLowerCase()}`),
        query: useRoute().query,
      });
    }

    if (response?.data?.value && typeof response?.data?.value === 'string' && route.path === '/') {
      const redirectUrl: URL = new URL(response?.data?.value);
      return navigateTo(redirectUrl.pathname, { external: !redirectUrl.pathname.startsWith('/shop') });
    }
  } catch (error) {
    console.warn('could not change store', error);
  }
}

function getCurrentOpenings(neoStore: SimpleStore) {
  const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  const openings = neoStore.openingHours;
  const currentDate = new Date();
  const today = currentDate.getDay() - 1;
  const now = currentDate.getHours() * 60 + currentDate.getMinutes();
  let firstOpenToday = null;
  let nextOpenToday = 1441;
  let lastOpenToday = null;
  let notOnBreak = false;

  for (let i = 0; i < openings?.length; i++) {
    const opening = openings[i];
    if (opening == null) {
      continue;
    }
    if (days.indexOf(opening.dayFrom) <= today && today <= days.indexOf(opening.dayTo)) {
      if (firstOpenToday == null || firstOpenToday > opening.timeFrom) {
        firstOpenToday = opening.timeFrom;
      }
      if (nextOpenToday > opening.timeFrom && opening.timeTo > now) {
        nextOpenToday = opening.timeFrom;
      }
      if (lastOpenToday == null || lastOpenToday < opening.timeTo) {
        lastOpenToday = opening.timeTo;
      }
      if (opening.timeFrom <= now && now <= opening.timeTo) notOnBreak = true;
    }
  }
  if (firstOpenToday == null || lastOpenToday == null) {
    return 'Heute geschlossen!';
  }
  if (firstOpenToday <= now && now <= lastOpenToday) {
    if (notOnBreak)
      return (
        "<div class='w-2.5 h-2.5 mr-1.5 inline-block rounded-3xl bg-green-700'></div>Geöffnet - schließt um "
        + convertMinutesToTimeString(lastOpenToday)
        + ' Uhr'
      );
    else
      return (
        "<div class='w-2.5 h-2.5 mr-1.5 inline-block rounded-3xl bg-red-700'></div>Geschlossen - öffnet um "
        + convertMinutesToTimeString(nextOpenToday)
        + ' Uhr'
      );
  } else if (now < firstOpenToday)
    return (
      "<div class='w-2.5 h-2.5 mr-1.5 inline-block rounded-3xl bg-red-700'></div>Geschlossen - öffnet um "
      + convertMinutesToTimeString(firstOpenToday)
      + ' Uhr'
    );
  else return "<div class='w-2.5 h-2.5 mr-1.5 inline-block rounded-3xl bg-red-700'></div> Heute bereits geschlossen!";
}
</script>
<style lang="postcss" scoped>
.entity {
  @apply flex gap-2 border p-4 bg-white;
}
.entity.active {
  @apply bg-secondary;
}
.location {
  @apply flex flex-col gap-4 grow;
}
.title {
  @apply flex justify-between font-bold;
}
.distance {
  @apply text-primary whitespace-nowrap;
}
.distance img {
  @apply inline-block align-baseline w-2;
}
.days {
  @apply font-bold;
}
.assign-trigger {
  @apply hidden;
}
.entity.active .assign-trigger {
  @apply block;
}
.assign-trigger button {
  @apply btn-primary w-full p-2;
}
.assign-trigger img {
  @apply inline-block align-baseline w-2;
}
</style>
